import produce from 'immer';
import { registerReducer } from '../../../StateSetup/RootReducer';

const { BFF_URL, GBCORE_CLIENT_NAME, GBCORE_CLIENT_VERSION } = window.__ENV__;

// Action names
const ACTION_NAME_PREFIX = 'action/getMemberBenefits';
export const REQUEST_ACTION_NAME = `${ACTION_NAME_PREFIX}/request`;
export const RESPONSE_ACTION_NAME = `${ACTION_NAME_PREFIX}/response`;
export const ERROR_ACTION_NAME = `${ACTION_NAME_PREFIX}/error`;

/**
 * Gets member benefit information for a member.
 */
export const GetMemberBenefits = () => {
    return async (dispatch, getState, { logger }) => {
        // step 1: Request Action
        dispatch({ type: REQUEST_ACTION_NAME });

        const { jwt, sessionCorrelationId, language } = getState().Authentication;
        const transactionId = logger.generateGuid();

        logger.info(`${ACTION_NAME_PREFIX}: Fetching member benefit data.`, { correlation: { transactionId } });

        // step 2: await Web service call
        const getBenefitsQuery = await fetch(`${BFF_URL}/api/v2`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'gb-authorization': jwt,
                'mli-session-correlation-id': sessionCorrelationId,
                'mli-correlation-id': transactionId,
                'mli-channel': 'web',
                'gb-client-name': GBCORE_CLIENT_NAME,
                'gb-client-version': GBCORE_CLIENT_VERSION,
                'Accept-Language': language,
            },
            body: JSON.stringify({
                operationName: 'GetMemberBenefitsQuery',
                variable: {},
                query: `
                    query GetMemberBenefitsQuery {
                        member {
                            benefits (federatedFilter: { status: { _equals: "ACTIVE" } }){
                                memberBenefitId
                                name
                                code
                                status
                                coverageType
                                cobRequired
                                hideCOBBenefit
                            }
                        }
                    }
                `,
            }),
        });

        const getBenefitsQueryResponse = await getBenefitsQuery.json();

        if (getBenefitsQueryResponse.errors) {
            const { message, code } = getBenefitsQueryResponse.errors[0];
            logger.critical(
                `${ACTION_NAME_PREFIX}: Failure when fetching member benefit data. Code: ${code} Reason: ${message}`,
                { correlation: { transactionId } }
            );
            return dispatch({
                type: ERROR_ACTION_NAME,
                code,
                message,
            });
        }
        // step 3: Response Action
        return dispatch({
            type: RESPONSE_ACTION_NAME,
            data: getBenefitsQueryResponse.data.member.benefits,
            success: true,
        });
    };
};

/**
 * Fired off when the request is initially sent to provide the loading state
 * @param {object} state
 */
export const requestReducer = produce((state) => {
    state.MemberData.isLoadingMemberBenefitData = true;
});

/**
 * Handles a successful response of member benefits data from gb-core-2.
 * @param {object} state
 * @param {object} action
 */
export const responseReducer = produce((state, action) => {
    state.MemberData.isLoadingMemberBenefitData = false;
    state.MemberData.memberBenefits = action.data;
});

/**
 * Fired off when the request encouters an error. An error thrown by this part of the code is considered a critical failure.
 * @param {object} state
 * @param {object} action
 */
export const errorReducer = produce((state, action) => {
    state.MemberData.isLoadingMemberBenefitData = false;
    state.Errors.initialLoad.hasError = true;
    state.Errors.initialLoad.code = action.code;
    state.Errors.initialLoad.message = action.message;
});

registerReducer(REQUEST_ACTION_NAME, requestReducer);
registerReducer(RESPONSE_ACTION_NAME, responseReducer);
registerReducer(ERROR_ACTION_NAME, errorReducer);
